// Libs
import React from 'react';
import moment from 'moment';

// Services & Helpers
import GlobalStateService from 'services/GlobalStateService';
import TextHelpers from 'helpers/TextHelpers';
import DateHelpers from 'helpers/DateHelpers';

// Components
import CourseTier from 'components/common/CourseTier';

//-------------------------------------------------------------------------------------------------------------------

class BookingsTable extends React.Component {

    constructor(props) {
        super(props);
    }

    shouldComponentUpdate(nextProps) {
        const {
            bookings,
            isEditMode
        } = this.props;

        // Re-render if different request ID
        if (nextProps.requestID != this.props.requestID) {
            return true;
        }

        // If edit mode changed, re-render
        if (nextProps.isEditMode != isEditMode) {
            return true;
        }

        // If arrays different length, re-render
        if (nextProps.bookings.length != bookings.length) {
            return true;
        }

        // Create a lookup of old booking positions
        const bookingIndices = {};
        for (var i = 0; i < bookings.length; i++) {
            const booking = bookings[i];
            bookingIndices[booking.id] = i;
        }

        // Run through new bookings to make sure they're all in the same place
        for (var i = 0; i < nextProps.bookings.length; i++) {
            const booking = nextProps.bookings[i];
            const index = bookingIndices[booking.id];
            
            // If booking missing or at a different position, re-render
            if (typeof(index) == 'undefined' || index != i) {
                return true;
            }

            // Mark it off the list
            delete bookingIndices[booking.id];
        }

        // If any old IDs are unaccounted for, re-render
        for (var b in bookingIndices) {
            return true;
        }

        return false;
    }

    render() {
        const {
            bookings,
            mode,
            isEditMode,
            onClick,
            instructorID,
            emptyText,
            vendorInvoiceStatus,
            toggleSelected,
            className,
            isATP,
            isSelecting,
            getIsSelectable,
            isScrolling
        } = this.props;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const getRowClass = (booking) => {
            if (this.props.mode == 'invoices') {
                if (booking.isSelected) {
                    return 'text-selected';
                } else if (booking.hasVendorPaid) {
                    return 'text-good';
                } else if (booking.daysSinceInvoicedVendor >= 30) {
                    return 'booking-overdue-vendor-payment';
                }
            }
            else if (this.props.mode == 'bills') {
                if (booking.bill && booking.bill.isOnHold) {
                    return 'bill-on-hold';
                } else if (booking.dateVendorPaid) {
                    return 'vendor-paid';
                } else if (booking.vendorInvoiceReference) {
                    return 'vendor-not-paid';
                } else {
                    return 'vendor-not-invoiced';
                }
            } else {
                if (booking.status == 'Enquiry') {
                    return 'booking-enquiry';
                }
            }
            return '';
        }

        if (bookings.length == 0) {
            return (
                <div className="empty-text">
                    {emptyText || 'Sorry, no ' + (mode == 'enquiries' ? 'enquiries' : 'bookings') + ' were found. Please adjust your filters.'}
                </div>
            );
        }

        const getVendorInvoiceStatus = (booking) => {
            if (booking.vendorInvoiceReference) {
                return (<>
                    Reference: {booking.vendorInvoiceReference}
                    {booking.dateInvoicedVendor &&
                        <div>Invoiced: {moment(booking.dateInvoicedVendor).format('DD/MM/YYYY')}</div>
                    }
                    {booking.hasVendorPaid ?
                        <div>Paid: {moment(booking.dateVendorPaid).format('DD/MM/YYYY')}</div> :
                        <div>
                            Not paid
                            {booking.daysSinceInvoicedVendor != 0 &&
                                <> ({booking.daysSinceInvoicedVendor} days)</>
                            }
                        </div>
                    }
                </>);
            } else if (isATP) {
                return 'Ready to invoice ATP / vendor';
            } else {
                return 'Ready to invoice vendor';
            }
        };

        const getBillStatus = (booking) => {
            if (booking.bill) {
                if (booking.bill.isOnHold) {
                    return 'On Hold';
                } else if (booking.bill.datePaid) {
                    return <>Paid on {moment(booking.bill.datePaid).format('DD/MM/YYYY')}</>;
                } else {
                    return (<>
                        Bill received<br/>
                        Est. date: {moment(booking.bill.expectedPaymentDate).format('DD/MM/YYYY')}
                    </>);
                }
            } else {
                return 'Not Submitted';
            }
        };

        // Determine what we can see
        const loginInfo = GlobalStateService.getValue('loginInfo');
        const showSelect = (mode == 'invoices' && vendorInvoiceStatus == 'NotPaid') || isSelecting;
        const showLMSID = (mode == 'bookings' || mode == 'invoices' || mode == 'bills' || mode == 'my-invoices');
        const showValue = (mode == 'bills' && loginInfo.isAccountantOrSiteAdmin);
        const showRevenue = (mode == 'invoices' || mode == 'bookings') && loginInfo.isAccountantOrSiteAdmin;
        const showProfit = (mode == 'bookings' && loginInfo.isSiteAdmin && !instructorID);
        const showBillStatus = (((mode == 'bookings' && loginInfo.isSiteAdmin) || mode == 'bills') && loginInfo.isAdmin);
        const showOurInvoice = ((mode == 'bookings' || mode == 'bills' || (mode == 'invoices' && vendorInvoiceStatus != 'NotInvoiced')) && loginInfo.isAccountantOrAdmin);
        const showCCPO = (mode == 'bookings' || mode == 'invoices' || mode == 'bills') && loginInfo.isAccountantOrAdmin;
        const showBookingStatus = (mode != 'enquiries');// && mode != 'invoices' && mode != 'bills');
        const showCustomerLanguageLocation = (mode != 'invoices' && mode != 'bills');
        const showCSATScore = (mode == 'bookings') && !loginInfo.isAccountant;
        const showTravelExpenses = (mode == 'invoices') && loginInfo.isAccountant;

        //(mode != 'bills' && isEditMode ? 'selectable' : '')
        // Render
        return (
            <div className={`sticky-table-container ${isScrolling ? 'scrolling' : ''}`}>
                <table className={'table table-bordered bookings-table sticky-table ' + (className || '')}>
                    <thead>
                        <tr>
                            {showSelect &&
                                <th className="select-column"></th>
                            }
                            <th className="geo-column">Geo</th>
                            {showLMSID &&
                                <th className="lmsid-column">
                                    {loginInfo.isVendor ? 'LMS ID' : 'Ref'}
                                </th>
                            }
                            {mode == 'enquiries' && <>
                                <th>Ref</th>
                                <th>Enq. Date</th>
                            </>}
                            {!instructorID &&
                                <th className="instructor-column">Instructor</th>
                            }
                            <th className="date-column">Dates</th>
                            <th className="course-column">Course</th>
                            {showCustomerLanguageLocation && <>
                                <th className="customer-column">Customer</th>
                                <th className="language-column">Language</th>
                                <th className="modality-column">Modality</th>
                                <th className="location-column">Location</th>
                            </>}
                            {showBookingStatus &&
                                <th className="status-column">Status</th>
                            }
                            {showCSATScore &&
                                <th className="csat-score-column">CSAT</th>
                            }
                            {showRevenue &&
                                <th className="revenue-column">Revenue</th>
                            }
                            {showProfit &&
                                <th className="profit-column">Profit</th>
                            }
                            {showValue &&
                                <th className="value-column">Value</th>
                            }
                            {isATP && <>
                                <th className="atp-name-column">ATP</th>
                                <th className="atp-fee-column">ATP Fee</th>
                            </>}
                            {showBillStatus &&
                                <th className="status-column">Bill Status</th>
                            }
                            {showOurInvoice && <>
                                <th className="status-column">Our Invoice</th>
                                {mode == 'invoices' &&
                                    <th className="message-column">Message</th>
                                }
                            </>}
                            {showCCPO &&
                                <th className="cc-po-column">CC / PO</th>
                            }
                            {showTravelExpenses &&
                                <th className="has-travel-expenses-column">Travel Exp.</th>
                            }
                        </tr>
                    </thead>
                    <tbody>
                        {bookings.map((booking, index) =>
                            <React.Fragment key={booking.id}>
                                <tr className={getRowClass(booking)}>
                                    {showSelect &&
                                        <td className="select-column">
                                            {(!getIsSelectable || getIsSelectable(booking)) && 
                                                <label
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={!!booking.isSelected}
                                                        onChange={e => toggleSelected(booking.id, e.target.checked)}
                                                    />
                                                </label>
                                            }
                                        </td>
                                    }
                                    <td>{booking.geoName}</td>
                                    {showLMSID &&
                                        <td>
                                            <button
                                                className="btn btn-secondary btn-sm"
                                                onClick={e => isEditMode ? onClick(booking) : null}
                                            >
                                                {booking.lmsid || booking.status}
                                            </button>
                                        </td>
                                    }
                                    {mode == 'enquiries' && <>
                                        <td>
                                            {!!booking.uniqueRef &&
                                                <button
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={e => isEditMode ? onClick(booking) : null}
                                                >
                                                    {booking.uniqueRef}
                                                </button>
                                            }
                                        </td>
                                        <td>{booking.enquiryDate ? moment(booking.enquiryDate).format('DD/MM/YYYY') : ''}</td>
                                    </>}
                                    {!instructorID &&
                                        <td>
                                            {booking.instructorName}
                                            {booking.instructorIsPriority && <>
                                                &nbsp;
                                                <span className="priority-instructor fa-solid fa-star" title="Priority instructor" />
                                            </>}
                                        </td>
                                    }
                                    <td className="no-break">
                                        {booking.dateRuns.map((dr, index) =>
                                            <div key={index}>
                                                {DateHelpers.formatDateRun(dr)}
                                            </div>
                                        )}
                                        {booking.numDays > 0 ? <>{booking.numDays} day{booking.numDays != 1 && 's'}</> : ''}
                                        {booking.status == 'Hold' &&
                                            <div className="booking-hold">(Hold)</div>
                                        }
                                        {booking.status == 'Tentative' &&
                                            <div className="booking-tentative">(Tentative)</div>
                                        }
                                    </td>
                                    <td>
                                        {booking.courseName}
                                        <CourseTier tier={booking.courseTier} />
                                    </td>
                                    {showCustomerLanguageLocation && <>
                                        <td>{booking.customerNames}</td>
                                        <td>{booking.language}</td>
                                        <td>{booking.modality}</td>
                                        <td>
                                            {booking.city ? <>{booking.city.name}, {booking.city.country.name}</> : booking.location}
                                        </td>
                                    </>}
                                    {showBookingStatus &&
                                        <td>
                                            {booking.status}
                                            {booking.status == 'Cancelled' && <>
                                                {booking.dateCancelled && <>
                                                    {' '}({moment(booking.dateCancelled).format('DD/MM/YYYY')})
                                                </>}
                                                {(mode == 'invoices' || mode == 'bills') && loginInfo.isAccountantOrAdmin && <>
                                                    {' '}(bill {booking.cancelledInvoiceProportion * 100}%)
                                                </>}
                                            </>}
                                        </td>
                                    }
                                    {showCSATScore &&
                                        <td className="csat-score-column">
                                            {booking.csatScore}
                                        </td>
                                    }
                                    
                                    {showRevenue &&
                                        <td>
                                            {!!booking.revenue && TextHelpers.formatCurrencyNew(booking.revenue)}
                                        </td>
                                    }
                                    {showProfit &&
                                        <td>
                                            {!!booking.profit && TextHelpers.formatCurrencyNew(booking.profit)}
                                        </td>
                                    }
                                    {showValue &&
                                        <td>
                                            {booking.cost}{' '}{booking.currency && booking.currency.code}
                                            {booking.hasTravelExpenses && <>
                                                {' '}<span className="fa fa-car-side" title="Includes travel expenses" />
                                            </>}
                                        </td>
                                    }
                                    {isATP && <>
                                        <td className="atp-name-column">{booking.partnerName}</td>
                                        <td className="atp-fee-column">
                                            {(booking.partnerName || booking.atpFee > 0) ? TextHelpers.formatCurrencyNew(booking.atpFee) : null}
                                        </td>
                                    </>}
                                    {showBillStatus &&
                                        <td className="status-column">
                                            {getBillStatus(booking)}
                                        </td>
                                    }
                                    {showOurInvoice && <>
                                        <td className="status-column vendor-status">
                                            {getVendorInvoiceStatus(booking)}
                                        </td>
                                        {mode == 'invoices' &&
                                            <td className="message-column">
                                                {booking.status == 'Cancelled' && booking.cancelledInvoiceProportion > 0 && <>
                                                    Billing as per cancellation T&C's
                                                </>}
                                                {booking.modality == 'ILT' &&
                                                    <span className="ilt-warning">
                                                        ILT course. Check for expenses
                                                    </span>
                                                }
                                            </td>
                                        }
                                    </>}
                                    {showCCPO &&
                                        <td className="cc-po-column">
                                            {booking.costCentre && <>
                                                {booking.costCentre.shortName}
                                                {!!booking.costCentre.poNumber && 
                                                    <>{' '}/ {booking.costCentre.poNumber}</>
                                                }
                                            </>}
                                        </td>
                                    }
                                    {showTravelExpenses &&
                                        <td className="has-travel-expenses-column">
                                            {booking.hasTravelExpenses ? 'Yes' : ''}
                                        </td>
                                    }
                                </tr>
                            </React.Fragment>
                        )}
                    </tbody>
                    </table>
            </div>
        );
    }
}

export default BookingsTable;
